import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useContent } from "../lib/content";
import { Lock, FileText, CheckCircle2, ExternalLink } from "lucide-react";

export default function TechRider() {
  const kicker = useContent("page.tech_rider.kicker", "/ 11 — TECH RIDER");
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const title = useContent("page.tech_rider.title", "TECH RIDER — PRIVATE");
  const intro = useContent(
    "page.tech_rider.intro",
    "Technical specifications are shared only with confirmed promoters. Enter the access code provided by artist management."
  );

  const verify = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      await api.post("/tech-rider/verify", { password });
      setAuthed(true);
      toast.success("Access granted");
    } catch {
      toast.error("Incorrect password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="tech-rider-page" className="px-6 md:px-12 py-20">
      <div className="max-w-[1100px] mx-auto">
        <div className="mb-12" data-testid="tr-header">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-silver-400" />
            <span className="font-title text-[0.65rem] tracking-[0.4em] text-silver-400">
              {kicker}
            </span>
          </div>
          <h1 className="font-title text-silver-glow text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-[0.06em] whitespace-pre-line">
            {title}
          </h1>
        </div>

        {!authed ? (
          <div className="hairline-t pt-10 max-w-md" data-testid="tr-gate">
            <div className="flex items-center gap-3 mb-6">
              <Lock size={16} className="text-silver-400" />
              <span className="font-title text-[0.62rem] tracking-[0.3em] text-silver-400">
                ACCESS REQUIRED
              </span>
            </div>
            <p className="font-body text-silver-400 text-sm leading-relaxed mb-8">
              {intro}
            </p>
            <form onSubmit={verify} className="flex flex-col gap-4" data-testid="tr-form">
              <label className="block">
                <span className="ip-label">Access Code</span>
                <input
                  type="password"
                  className="ip-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                  data-testid="tr-password"
                />
              </label>
              <button
                type="submit"
                className="btn-sharp self-start mt-4"
                disabled={loading}
                data-testid="tr-submit"
              >
                {loading ? "Verifying…" : "Unlock"}
              </button>
            </form>
            <p className="font-body text-silver-600 text-xs mt-6">
              Don't have the code?{" "}
              <a href="/booking" className="text-silver-300 underline">
                Contact via Booking
              </a>
              .
            </p>
          </div>
        ) : (
          <TechRiderContent />
        )}
      </div>
    </div>
  );
}

function TechRiderContent() {
  const footerNote = useContent(
    "page.tech_rider.footer_note",
    "Any deviation from this rider must be agreed in writing with artist management at least 14 days before the event."
  );
  const pdfUrl = useContent("page.tech_rider.pdf_url", "");
  const pdfLabel = useContent("page.tech_rider.pdf_label", "Download Full Tech Rider (PDF)");
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const fullPdf = pdfUrl
    ? pdfUrl.startsWith("http")
      ? pdfUrl
      : `${BACKEND_URL}${pdfUrl}`
    : "";

  // Bios
  const biosTitle = useContent("page.tech_rider.bios_title", "BIOS");
  const bio1Title = useContent("page.tech_rider.bio1_title", "Bio Inner Pulse — Techno");
  const bio1Body = useContent("page.tech_rider.bio1_body", "");
  const bio2Title = useContent("page.tech_rider.bio2_title", "Bio Sandrita — Prog / Psychedelic");
  const bio2Body = useContent("page.tech_rider.bio2_body", "");
  const bio3Title = useContent("page.tech_rider.bio3_title", "Bio Downtempo Live Project");
  const bio3Body = useContent("page.tech_rider.bio3_body", "");
  const bios = [
    { title: bio1Title, body: bio1Body },
    { title: bio2Title, body: bio2Body },
    { title: bio3Title, body: bio3Body },
  ].filter((b) => b.title && b.body);

  // Tech rider sections
  const riderTitle = useContent("page.tech_rider.rider_title", "TECH RIDER");
  const s1Title = useContent("page.tech_rider.section1_title", "DJ Setup");
  const s1Body = useContent("page.tech_rider.section1_body", "");
  const s2Title = useContent("page.tech_rider.section2_title", "Analog Connection");
  const s2Body = useContent("page.tech_rider.section2_body", "");
  const s3Title = useContent("page.tech_rider.section3_title", "Fees");
  const s3Body = useContent("page.tech_rider.section3_body", "");
  const s4Title = useContent("page.tech_rider.section4_title", "");
  const s4Body = useContent("page.tech_rider.section4_body", "");
  const sections = [
    { title: s1Title, body: s1Body },
    { title: s2Title, body: s2Body },
    { title: s3Title, body: s3Body },
    { title: s4Title, body: s4Body },
  ].filter((s) => s.title && s.body);

  return (
    <div className="hairline-t pt-10 text-white" data-testid="tr-content">
      {/* Access banner */}
      <div className="flex flex-wrap items-center gap-4 mb-10" data-testid="tr-access-granted">
        <CheckCircle2 size={18} className="text-white" />
        <span className="font-title text-[0.65rem] tracking-[0.3em] text-white">
          ACCESS GRANTED — CONFIDENTIAL
        </span>
      </div>

      {/* Big download CTA */}
      {fullPdf && (
        <a
          href={fullPdf}
          target="_blank"
          rel="noreferrer"
          download
          className="group block hairline mb-16 p-8 md:p-10 bg-black hover:bg-white/[0.02] transition-colors"
          data-testid="tr-download-pdf"
        >
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="hairline w-14 h-14 flex items-center justify-center shrink-0">
                <FileText size={22} className="text-white" />
              </div>
              <div>
                <div className="font-title text-[0.6rem] tracking-[0.32em] text-white/60 uppercase mb-2">
                  / Confidential Document
                </div>
                <div className="font-title text-base md:text-xl tracking-[0.14em] text-white uppercase leading-tight">
                  {pdfLabel}
                </div>
                <div className="font-body text-xs text-white/50 mt-2">
                  Bio · Tech Rider · Fees — PDF
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 font-title text-[0.65rem] tracking-[0.3em] uppercase text-white group-hover:translate-x-1 transition-transform">
              <ExternalLink size={14} />
              <span>Download</span>
            </div>
          </div>
        </a>
      )}

      {/* BIOS */}
      {bios.length > 0 && (
        <section className="mb-16" data-testid="tr-bios">
          <div className="flex items-center gap-2 hairline-b pb-3 mb-10">
            <FileText size={14} className="text-white/70" />
            <h2 className="font-title text-sm tracking-[0.2em] text-white uppercase">
              {biosTitle}
            </h2>
          </div>
          <div className="flex flex-col gap-12">
            {bios.map((b, i) => (
              <article key={i} className="grid md:grid-cols-12 gap-8" data-testid={`tr-bio-${i}`}>
                <div className="md:col-span-3">
                  <div className="font-title text-[0.62rem] tracking-[0.3em] text-white/40 mb-2">
                    / {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-title text-base md:text-lg tracking-[0.12em] text-white leading-snug">
                    {b.title}
                  </h3>
                </div>
                <p
                  className="md:col-span-9 font-body text-white/90 text-sm md:text-base leading-relaxed whitespace-pre-line"
                  style={{ lineHeight: 1.8 }}
                >
                  {b.body}
                </p>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* TECH RIDER SECTIONS */}
      {sections.length > 0 && (
        <section className="mb-12" data-testid="tr-rider">
          <div className="flex items-center gap-2 hairline-b pb-3 mb-10">
            <FileText size={14} className="text-white/70" />
            <h2 className="font-title text-sm tracking-[0.2em] text-white uppercase">
              {riderTitle}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-10">
            {sections.map((s, i) => (
              <section key={i} className="flex flex-col gap-4" data-testid={`tr-section-${i}`}>
                <div className="flex items-center gap-3">
                  <span className="font-title text-[0.62rem] tracking-[0.3em] text-white/40">
                    / {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-title text-sm tracking-[0.18em] text-white uppercase">
                    {s.title}
                  </h3>
                </div>
                <ul className="font-body text-white/90 text-sm leading-relaxed space-y-2 hairline-t pt-3">
                  {s.body
                    .split("\n")
                    .map((l) => l.trim())
                    .filter(Boolean)
                    .map((line, j) => (
                      <li key={j} className="flex gap-3">
                        <span className="text-white/40 shrink-0">—</span>
                        <span>{line}</span>
                      </li>
                    ))}
                </ul>
              </section>
            ))}
          </div>
        </section>
      )}

      {/* FOOTER */}
      <div
        className="hairline-t mt-12 pt-8 flex flex-wrap items-center justify-between gap-4"
        data-testid="tr-footer"
      >
        <p className="font-body text-white/60 text-xs max-w-md">{footerNote}</p>
        {fullPdf && (
          <a
            href={fullPdf}
            target="_blank"
            rel="noreferrer"
            download
            className="btn-ghost flex items-center gap-2 text-white"
            data-testid="tr-download-pdf-2"
          >
            <ExternalLink size={14} /> {pdfLabel}
          </a>
        )}
      </div>
    </div>
  );
}
